import { getPreciseContextAroundPdfSelection, smartTruncate } from '@readsavor/utils';
import { PdfParagraphAdapter, TextParagraphAdapter } from '../adapters/index.js';
/**
 * Prepares arguments required for initiating a translation request.
 *
 * This function takes the user's selection details and the document's paragraphs
 * to determine the text to translate, the immediate paragraph of selection,
 * and a window of surrounding paragraphs for contextual understanding.
 *
 * @param params - The input parameters for argument preparation.
 * @returns The prepared arguments for translation, or null if inputs are invalid (e.g., no text selected).
 */
export function prepareTranslationArgs(params) {
    const { selection, allDocumentParagraphs } = params;
    if (!selection || !selection.selectedText.trim()) {
        console.warn('[prepareTranslationArgs] No selection or empty selected text.');
        return null;
    }
    const trimmedSelectedText = selection.selectedText.trim();
    const rawDocumentParagraphs = Array.isArray(allDocumentParagraphs) ? allDocumentParagraphs : [];
    // --- Start: Adapt all paragraphs to a unified interface ---
    let adaptedParagraphs = [];
    if (rawDocumentParagraphs.length > 0) {
        if (selection.type === 'text') {
            adaptedParagraphs = rawDocumentParagraphs.map(p => new TextParagraphAdapter(p));
        }
        else {
            adaptedParagraphs = rawDocumentParagraphs.map(p => new PdfParagraphAdapter(p));
        }
    }
    // --- End: Adapt all paragraphs ---
    const currentParagraphId = selection.type === 'text'
        ? selection.location.paragraphData.id
        : selection.location.paragraphId;
    const currentParagraphForContext = adaptedParagraphs.find(p => p.getId() === currentParagraphId);
    if (!currentParagraphForContext) {
        console.warn(`[prepareTranslationArgs] Could not find current paragraph with ID ${currentParagraphId} in the adapted list.`);
        return null;
    }
    // --- Start: Calculate surrounding context window ---
    const selectedIndexInFullList = adaptedParagraphs.findIndex(p => p.getId() === currentParagraphId);
    const windowStartIndex = Math.max(0, selectedIndexInFullList - 5);
    const windowEndIndex = Math.min(adaptedParagraphs.length, selectedIndexInFullList + 6);
    const surroundingParagraphsContext = adaptedParagraphs.slice(windowStartIndex, windowEndIndex);
    // --- End: Calculate surrounding context window ---
    // --- Start: Build the precise contextual prompt ---
    let prefix = '';
    let suffix = '';
    const selectedIndexInWindow = surroundingParagraphsContext.findIndex(p => p.getId() === currentParagraphId);
    for (let i = 0; i < selectedIndexInWindow; i++) {
        prefix += surroundingParagraphsContext[i].getContent() + ' ';
    }
    for (let i = selectedIndexInWindow + 1; i < surroundingParagraphsContext.length; i++) {
        suffix += surroundingParagraphsContext[i].getContent() + ' ';
    }
    let selectionStart = 0;
    let selectionEnd = 0;
    if (selection.type === 'text') {
        selectionStart = selection.location.startOffset;
        selectionEnd = selection.location.endOffset;
        const paragraphText = currentParagraphForContext.getContent();
        prefix += paragraphText.substring(0, selectionStart);
        suffix = paragraphText.substring(selectionEnd) + ' ' + suffix;
        // Apply smart truncation to text context (same limits as PDF: 50 CJK, 200 English)
        prefix = smartTruncate(prefix.trim(), 50, 200, true);
        suffix = smartTruncate(suffix.trim(), 50, 200, false);
    }
    else if (selection.type === 'pdf') {
        const words = currentParagraphForContext.getWords() || [];
        const { startWordId, endWordId, startWordOffset, endWordOffset } = selection.location;
        let intraWordPrefix = '';
        let intraWordSuffix = '';
        const startWordObject = words.find(w => w.id === startWordId);
        const endWordObject = words.find(w => w.id === endWordId);
        if (startWordObject) {
            intraWordPrefix = startWordObject.text.substring(0, startWordOffset);
        }
        if (endWordObject) {
            intraWordSuffix = endWordObject.text.substring(endWordOffset);
        }
        const plainParagraphsForContext = surroundingParagraphsContext.map(p => ({
            content: p.getContent(),
            words: p.getWords() || [],
        }));
        const context = getPreciseContextAroundPdfSelection(plainParagraphsForContext, selectedIndexInWindow, startWordId, endWordId, intraWordPrefix, intraWordSuffix);
        prefix = context.beforeText;
        suffix = context.afterText;
        // Note: selectionStart and selectionEnd remain 0 for PDF selections
        // as they are not applicable for the current database schema.
        // This is expected until Stage 4 of the refactoring is complete.
    }
    // --- End: Build the precise contextual prompt ---
    return {
        textToTranslate: trimmedSelectedText,
        currentParagraphForContext,
        surroundingParagraphsContext,
        contextualPrompt: {
            prefix: prefix.trim(),
            selection: trimmedSelectedText,
            suffix: suffix.trim(),
        },
        originalSelection: selection,
    };
}
//# sourceMappingURL=prepareTranslationArgs.js.map